// İSG uzmanı portalı — "Bugün" paneli (S32b `docs/reviews/S32b-gate-desk-relayout.md` §4, O1:
// sol sütun ARTIK yalnız bu panel — sekme çubuğu KALKTI). `GET /security/entries?result=all`
// (§29.5 S32b) artık hem içeri alınanları HEM reddedilen denemeleri AYNI listede döner; bu panel
// hem tahliye yoklamasıdır (`Girenler` süzgeci) hem de "Son geçişler" rayının kalkmasıyla onun
// işini de devralan tek kayıttır (bir reddin ekranda görünür KALDIĞI tek yer).
//
// **Sayım kuralı (§29.5 normatif) — ekranda TEKRARLANIR, yeniden HESAPLANMAZ.** Sayaçlar
// `useSecurityDayEntries.ts`'ten HAZIR gelir (`allowedCount`/`deniedCount`/…) — bu panel onları
// TÜRETMEZ, yalnız GÖSTERİR. `Tümü` süzgeci VARSAYILANDIR (owner kararı: "today's everything,
// rejections included") — `Girenler` bir AĞ isteği DEĞİL, aynı dizinin istemci-taraflı görünümü.
//
// **Kesilme uyarısı (§29.5 normatif, ZORUNLU) — `Denied` satırları en yeni 200 ile sınırlıdır;**
// kesilenler sıralamanın (`at` azalan) sonundaki EN ESKİ rettlerdir ("ret ufku"). `truncated` bu
// yüzden İSTEĞE BAĞLI bir detay değil, gösterilmek ZORUNDA bir uyarıdır.
//
// **Satır ARTIK tıklanabilir (S32d Part Two, `docs/reviews/S32d-verdict-and-detail.md` §7).**
// Ad hücresi gerçek bir `<button>` — `EntryDetailModal.tsx`'i AÇAR (salt-okuma, `GET
// /security/entries/{uuid}`). Modal seçili SATIR OBJESİNİN kendisini tutar (`selectedRow`), bir
// dizi indeksi DEĞİL — bu sorgu her yeni okutmada geçersiz kılındığından bir indeks modal
// AÇIKKEN sessizce başka bir kişiyi gösterirdi (tasarım spec'i §7.3, F9 defekti).
//
// **S32e Part Three (`docs/reviews/S32e-evidence-and-layout.md` §5/§6, Variant 1 — T.C. SÜTUNU
// YOK) — `table-layout: auto` → `table-fixed`, yedi sütunun YEDİSİ de EXPLICIT genişlikte.**
// Owner'ın "titriyor" bulgusu font DEĞİL (`tabular-nums` `Saat`'te zaten VARDI) — sorumlu
// `table-layout: auto`'nun esnek sütunları HER satırın içeriğinden yeniden HESAPLAMASI (G5); bu
// liste her okutmada geçersiz kılındığından (`SecuritySiteSession.tsx`) uzun bir ad tek başına
// `Ad Soyad`/`Yöntem`/`Sebep`i yeniden GENİŞLETİR. `table-fixed` sabit: genişlik satırlardan
// DEĞİL, `<th>`lerden gelir — bedeli, taşan bir hücrenin artık İTMEK yerine KIRPMASI, bu yüzden
// kullanıcı-türetili HER hücre `truncate` + `title` taşımak ZORUNDADIR (S32c E7, zaten kural).
// **Taşeron sütunu YENİ** (`security.day.col.contractor`) — S32c §5.3'ün "aynı-ad çakışması"
// sorununu VEREN DEĞİL, zaten telde olan veriyle (`contractorName`) ÇÖZER; `xl`'İN ALTINDA
// eskisi gibi ad hücresinin alt satırına (`HardHat` ikonu + `sr-only` etiketle) DÖNER — sub-`xl`
// görünüm BYTE-FOR-BYTE değişmeden kalır. Genişlikler (border-box, hücre dolgusu DÂHİL): Durum
// `w-36`(9rem, ESKİ 7.5rem — Almanca `Eingetreten` 135px'te bir 120px sütunu TAŞARDI, G6),
// Saat `w-20`(5rem, ESKİ 4rem — Almanca `UHRZEIT`), Ad Soyad esnek (`min-w-[14rem]`, KALAN
// boşluğun TAMAMINI alır), Taşeron `w-56`(14rem), Yöntem `w-32`(8rem), Sebep `w-48`(12rem),
// chevron `w-8`(2rem). Toplam sabit 800px + esnek ad sütunu — 991px'te (1280+kenar çubuğu)
// 191px boşlukla, 1232px'te (1280 tam ekran) rahatça SIĞAR (G4 kapanır).
//
// **`Yöntem`in `<th>`'ı düzeltildi** — eski hâlde `<th>` `hidden sm:table-cell` TAŞIMIYORDU
// (`<td>`si taşıyordu), yani `< sm`'de gövde hücresi GİZLENİRKEN başlık GÖRÜNÜR kalıyordu —
// sütun hizası bozulurdu. `table-fixed` altında bu daha da GÖRÜNÜR olurdu (genişlik İLK
// satırdan/`<th>`den ALINIR); `<th>` artık `<td>` ile AYNI `hidden sm:table-cell` çiftini taşır.

import { Alert, Spinner, Tag, currentCulture } from '@isgportal/shared';
import type { SecurityEntryRowDto } from '@isgportal/shared';
import { ChevronRight, CircleCheck, HardHat, OctagonX, ShieldAlert, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterChipGroup } from '../../components/FilterChipGroup';
import { PanelHeader } from '../../components/PanelHeader';
import { entryReasonTone } from '../../components/contractors/tone';
import { formatTime } from '../../lib/format';
import { reasonLabel } from '../../lib/securityReason';
import type { SecurityDayEntriesResult } from '../../lib/useSecurityDayEntries';
import { EntryDetailModal } from './EntryDetailModal';
import { MethodMarker } from './MethodMarker';

type TFunction = (key: string, options?: Record<string, unknown>) => string;
type ViewFilter = 'all' | 'allowed';

export interface DayListTabProps {
  entries: SecurityDayEntriesResult;
}

export function DayListTab({ entries }: DayListTabProps) {
  const { t } = useTranslation();
  const culture = currentCulture();
  const [filter, setFilter] = useState<ViewFilter>('all');

  const rows =
    filter === 'allowed' ? entries.rows.filter((r) => r.result === 'Allowed') : entries.rows;

  const filters: { value: ViewFilter; label: string; count: number }[] = [
    { value: 'all', label: t('security.day.filter.all'), count: entries.rows.length },
    { value: 'allowed', label: t('security.day.filter.allowed'), count: entries.allowedCount },
  ];

  // S32d Part Two — modal `row` OBJESİNİN KENDİSİNİ tutar, bir DİZİ İNDEKSİ DEĞİL (tasarım
  // spec'i §7.3): `entries` her yeni okutma/TC sonucunda geçersiz kılınır (`SecuritySiteSession`
  // dosya başı notu), bir indeks modal AÇIKKEN sessizce farklı bir kişiye taşınırdı. `uuid`
  // sözleşmenin verdiği KALICI kimlik (§29.5) — kapanışta `null`'a döner, sorgu da bununla
  // birlikte devre dışı kalır (`EntryDetailModal.tsx`).
  const [selectedRow, setSelectedRow] = useState<SecurityEntryRowDto | null>(null);

  return (
    <div className="flex flex-col rounded-sm border border-border bg-surface shadow-sm">
      <PanelHeader
        icon={Users}
        title={t('security.panel.today.title')}
        right={
          entries.forbidden ? null : (
            <FilterChipGroup
              options={filters}
              value={filter}
              onChange={setFilter}
              groupLabel={t('security.day.filter.groupLabel')}
            />
          )
        }
      />

      {entries.forbidden ? (
        <div className="flex min-h-[16rem] flex-col items-center justify-center gap-3 px-5 py-8 text-center">
          <ShieldAlert className="h-8 w-8 text-muted" aria-hidden="true" />
          <p className="max-w-sm text-sm text-muted">{t('security.gate.forbidden.body')}</p>
        </div>
      ) : entries.isPending ? (
        <div className="flex min-h-[16rem] items-center justify-center px-5 py-8">
          <Spinner label={t('security.common.loading')} />
        </div>
      ) : entries.error ? (
        <div className="flex min-h-[16rem] flex-col items-center justify-center gap-3 px-5 py-8">
          <Alert tone="danger">{t('security.day.error')}</Alert>
          <button
            type="button"
            onClick={entries.refetch}
            className="rounded text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('security.gate.retry')}
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex min-h-[16rem] items-center justify-center px-5 py-8 text-center">
          <p className="text-sm text-muted">{t('security.day.empty')}</p>
        </div>
      ) : (
        <>
          {entries.truncated ? (
            <div className="px-5 pt-3">
              <Alert tone="warning">{t('security.day.truncated')}</Alert>
            </div>
          ) : null}

          <div className="max-h-[26rem] overflow-y-auto overflow-x-auto">
            <table className="w-full table-fixed border-collapse text-sm">
              <caption className="sr-only">{t('security.panel.today.title')}</caption>
              <thead className="sticky top-0 z-[var(--z-base)] bg-surface-muted">
                <tr>
                  <th
                    scope="col"
                    className="w-36 whitespace-nowrap px-4 py-2 text-left text-xs font-semibold text-muted"
                  >
                    {t('security.day.col.status')}
                  </th>
                  <th
                    scope="col"
                    className="w-20 whitespace-nowrap px-2 py-2 text-left text-xs font-semibold text-muted"
                  >
                    {t('security.day.col.time')}
                  </th>
                  <th
                    scope="col"
                    className="min-w-[14rem] px-2 py-2 text-left text-xs font-semibold text-muted"
                  >
                    {t('security.day.col.name')}
                  </th>
                  {/* Taşeron sütunu (S32e §6.1) — YALNIZ `xl`+; altındaki kırılmada ad hücresinin
                      alt satırına döner (aşağı bkz., `DayRow`). */}
                  <th
                    scope="col"
                    className="hidden w-56 whitespace-nowrap px-4 py-2 text-left text-xs font-semibold text-muted xl:table-cell"
                  >
                    {t('security.day.col.contractor')}
                  </th>
                  <th
                    scope="col"
                    className="hidden w-32 whitespace-nowrap px-2 py-2 text-left text-xs font-semibold text-muted sm:table-cell"
                  >
                    {t('security.day.col.method')}
                  </th>
                  <th
                    scope="col"
                    className="hidden w-48 whitespace-nowrap px-4 py-2 text-left text-xs font-semibold text-muted xl:table-cell"
                  >
                    {t('security.day.col.reason')}
                  </th>
                  {/* Detay sütunu (S32d) — başlıksız: `ChevronRight` yalnız görsel bir "açılır"
                      ipucudur, satırın gerçek erişilebilir kontrolü ad hücresindeki `<button>`. */}
                  <th scope="col" className="w-8 px-2 py-2">
                    <span className="sr-only">{t('security.day.col.detail')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  // `uuid` (S32d, §29.5) — satırın KALICI, sözleşme-verdiği kimliği; eski
                  // `at|personnelName|method|result|index` dörtlüsü (çakışabilen, sayfa
                  // yenilenince kaybolan) ARTIK GEREKMİYOR.
                  <DayRow
                    key={row.uuid}
                    row={row}
                    t={t}
                    culture={culture}
                    onOpen={setSelectedRow}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-1 border-t border-border px-5 py-2.5 text-xs text-muted">
            <p>
              {t('security.day.summary', {
                total: entries.allowedCount,
                security: entries.securityMediatedCount,
                selfAsserted: entries.selfAssertedCount,
              })}
            </p>
            {entries.deniedCount > 0 ? (
              <p>{t('security.day.deniedNote', { count: entries.deniedCount })}</p>
            ) : null}
            <p>{t('security.day.methodDisclaimer')}</p>
          </div>
        </>
      )}

      <EntryDetailModal
        open={selectedRow != null}
        onOpenChange={(next) => {
          if (!next) {
            setSelectedRow(null);
          }
        }}
        row={selectedRow}
      />
    </div>
  );
}

function DayRow({
  row,
  t,
  culture,
  onOpen,
}: {
  row: SecurityEntryRowDto;
  t: TFunction;
  culture: string;
  onOpen: (row: SecurityEntryRowDto) => void;
}) {
  const isAllowed = row.result === 'Allowed';
  const reasonText = isAllowed ? null : reasonLabel(row.reason, t);
  // Bug fix (code review) — eski hâl `isAllowed || entryReasonTone(...) === 'danger'` idi;
  // `===` `||`'dan daha SIKI bağlandığından `isAllowed` tek başına koşulu HER Allowed satır
  // için `'text-danger'`ya çözüyordu (bugüne dek zararsız — `reasonText` zaten `null`, ama bu
  // hücreye yarın bir şey basan ilk kişiyi ısırırdı). Niyet: reddedilmiş VE sebebi "danger"
  // sınıfındaysa kırmızı.
  const reasonTone =
    !isAllowed && entryReasonTone(row.reason) === 'red' ? 'text-danger' : 'text-muted';

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer kolaylığı — erişilebilir kontrol ad hücresindeki gerçek <button> (tasarım spec'i §7.3).
    <tr
      className="border-t border-border first:border-t-0 hover:bg-surface-muted/60"
      onClick={() => onOpen(row)}
    >
      <td className="whitespace-nowrap px-4 py-2 align-middle">
        <Tag color={isAllowed ? 'green' : 'red'}>
          {isAllowed ? (
            <CircleCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          ) : (
            <OctagonX className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          )}
          {isAllowed ? t('security.day.status.allowed') : t('security.day.status.denied')}
        </Tag>
      </td>
      <td className="whitespace-nowrap px-2 py-2 align-middle tabular-nums text-muted">
        {formatTime(row.at, culture)}
      </td>
      <td className="px-2 py-2 align-middle">
        <div className="min-w-[9rem]">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(row);
            }}
            aria-label={t('security.detail.open', {
              name: row.personnelName,
              time: formatTime(row.at, culture),
            })}
            className="truncate text-left font-medium text-fg underline-offset-2 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title={row.personnelName}
          >
            {row.personnelName}
          </button>
          {/* Taşeron alt satırı (S32c §5.3, S32e §6.1'in fold kuralı) — ARTIK `xl:hidden`:
              `xl`+'de kendi sütunu var (aşağı bkz.), altında eskisi gibi buraya döner (sub-`xl`
              görünüm BYTE-FOR-BYTE değişmedi). VERİYE göre koşulludur: `null` iken satır
              TAMAMEN OMİTİLİR, nötr bir tire BASILMAZ (tasarım dokümanı §5.2/§5.3). */}
          {row.contractorName ? (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-muted xl:hidden">
              <HardHat className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="sr-only">{t('security.contractor.srLabel')}</span>
              <span className="min-w-0 max-w-[12rem] truncate" title={row.contractorName}>
                {row.contractorName}
              </span>
            </p>
          ) : null}
          {/* Fold order (§4.2, S32e §6.1 madde 2/3 ile GENİŞLETİLDİ — tek yerde belirtilir,
              kırılma noktası başına yeniden KARARLAŞTIRILMAZ): `< xl` altında `Sebep` VE
              `Taşeron` kendi sütunlarını bırakıp bu alt satıra döner; `< sm` altında `Yöntem` de
              katılır. Hiçbiri KAYBOLMAZ, yalnız KONUM değiştirir. */}
          <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            {reasonText ? (
              <span className={`truncate text-xs xl:hidden ${reasonTone}`} title={reasonText}>
                {reasonText}
              </span>
            ) : null}
            <span className="sm:hidden">
              <MethodMarker method={row.method} />
            </span>
          </p>
        </div>
      </td>
      {/* Taşeron sütunu (S32e §6.1, YENİ) — `xl`+ ONLY. `null` → BOŞ hücre: ne tire ne glif
          (S32c §13.5 — bir tire "uygulanamaz" der, `null` "sunucu çözemedi" demektir, ikisini
          karıştırmak yanlış bir iddia olurdu). İkon/`sr-only` etiket burada YOK — `<th>` zaten
          sütunun etiketi. */}
      <td className="hidden px-4 py-2 align-middle xl:table-cell">
        {row.contractorName ? (
          <span className="block truncate text-xs text-muted" title={row.contractorName}>
            {row.contractorName}
          </span>
        ) : null}
      </td>
      <td className="hidden px-2 py-2 align-middle sm:table-cell">
        <MethodMarker method={row.method} />
      </td>
      <td className="hidden px-4 py-2 align-middle xl:table-cell">
        {reasonText ? (
          <span className={`block max-w-[12rem] truncate text-xs ${reasonTone}`} title={reasonText}>
            {reasonText}
          </span>
        ) : (
          <span className="text-xs text-muted">—</span>
        )}
      </td>
      {/* Dokunma/işaretçi için HOVER-DIŞI ipucu (tasarım spec'i §7.3) — `hover:bg-surface-muted/60`
          satır tintiyle karışıp TEK sinyal olmasın diye ikinci, her zaman görünür bir gösterge. */}
      <td className="px-2 py-2 align-middle">
        <ChevronRight className="h-4 w-4 text-muted" aria-hidden="true" />
      </td>
    </tr>
  );
}
